// Referral attribution + own-code helpers. Anonymous, device-based, no accounts.
//
// - `spotdrop_referred_by` (localStorage): the code of the referrer that first
//   introduced this device. NEVER overwritten once set.
// - `spotdrop_own_referral_code` (localStorage): the code owned by this device,
//   fetched lazily from the backend.
// - `spotdrop_referral_visit_ts` (sessionStorage): guard so a given code triggers
//   at most one /referrals/visit call per session (per code).
//
// Also parses `?ref=CODE` and `/join/CODE` URLs, cleans the URL, then fires
// visit+attribute in the background. Failures are silent — referrals must
// never block the user experience.

import { api } from "./api";

const KEY_REFERRED_BY = "spotdrop_referred_by";
const KEY_OWN_CODE = "spotdrop_own_referral_code";
const SESSION_VISIT_PREFIX = "spotdrop_ref_visit_";

const CODE_RE = /^[A-Z0-9]{6}$/;

function safeUpper(code) {
  return typeof code === "string" ? code.trim().toUpperCase() : "";
}

function isValidCode(code) {
  return CODE_RE.test(safeUpper(code));
}

export function getReferredBy() {
  try {
    return localStorage.getItem(KEY_REFERRED_BY) || null;
  } catch {
    return null;
  }
}

function setReferredByOnce(code) {
  const clean = safeUpper(code);
  if (!isValidCode(clean)) return null;
  try {
    // Do NOT overwrite an existing valid attribution.
    const existing = localStorage.getItem(KEY_REFERRED_BY);
    if (existing && isValidCode(existing)) return existing;
    localStorage.setItem(KEY_REFERRED_BY, clean);
    return clean;
  } catch {
    return null;
  }
}

export function getOwnReferralCode() {
  try {
    const v = localStorage.getItem(KEY_OWN_CODE);
    return isValidCode(v) ? v : null;
  } catch {
    return null;
  }
}

function cacheOwnCode(code) {
  const clean = safeUpper(code);
  if (!isValidCode(clean)) return;
  try {
    localStorage.setItem(KEY_OWN_CODE, clean);
  } catch {
    /* ignore */
  }
}

/** Fetch (or lazily create) the referral code owned by this device.
 *  Returns the aggregate object `{ code, visits, unique_referred, qualified_count }`
 *  or `null` on network failure. Result is cached in localStorage. */
export async function fetchMyReferral() {
  try {
    const res = await api.get("/referrals/mine");
    if (res?.data?.code) {
      cacheOwnCode(res.data.code);
      return res.data;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Copy shared by every "Invite Friends & Family" entry point. */
export const INVITE_FF_MESSAGE =
  "Parking in Malta? \uD83D\uDE97\n" +
  "I'm using SpotDrop to share and find parking spots.\n" +
  "Join me here:";

/** Build a shareable link for a code, using the same origin the visitor is on.
 *  Uses `/join/CODE` (cleaner for WhatsApp) as the public form. */
export function buildJoinUrl(code) {
  const clean = safeUpper(code);
  if (!isValidCode(clean)) return "";
  if (typeof window === "undefined") return `/join/${clean}`;
  return `${window.location.origin}/join/${clean}`;
}

/** WhatsApp deep-link (works on mobile + web). */
export function buildWhatsAppShareUrl(code) {
  const link = buildJoinUrl(code);
  if (!link) return "";
  const msg =
    "\u26FD Win \u20AC50 of fuel with SpotDrop!\n" +
    "We're giving away 2 \u00D7 \u20AC50 fuel vouchers in Malta.\n" +
    "Join through my link, use SpotDrop and collect chances to win.\n" +
    "Free to enter. No account needed.\n" +
    link;
  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
}

/** Trigger the best available share flow for THIS device's referral link.
 *  Strategy (in order):
 *    1. Native Web Share API (`navigator.share`) — mobile Safari + Chrome
 *    2. WhatsApp deep-link fallback (`wa.me/?text=…`)
 *    3. Copy to clipboard + toast
 *
 *  `notify` is an optional `(kind, message) => void` callback so callers can
 *  wire this into their toast system without pulling `sonner` into this lib.
 *  Returns the strategy actually used: 'native' | 'whatsapp' | 'copy' | 'none'.
 */
export async function shareReferral(notify, options = {}) {
  const say = typeof notify === "function" ? notify : () => {};

  // 1. Ensure we have a code — fetch/create if the cache is empty.
  let code = getOwnReferralCode();
  if (!code) {
    const data = await fetchMyReferral();
    code = data?.code || null;
  }
  if (!code) {
    say("error", "Could not create your referral link. Please try again.");
    return "none";
  }

  const link = buildJoinUrl(code);
  const defaultText =
    "\u26FD Win \u20AC50 of fuel with SpotDrop! " +
    "We're giving away 2 \u00D7 \u20AC50 fuel vouchers in Malta. " +
    "Join through my link, use SpotDrop and collect chances to win. " +
    "Free to enter. No account needed.";
  const shareTitle = options.title || "SpotDrop Malta \u2014 \u20AC100 Fuel Giveaway";
  const shareText = options.text || defaultText;
  const copyMessage = options.copyMessage || "Referral link copied";

  // 2. Native Web Share (best UX on iOS Safari + modern Android Chrome).
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: link,
      });
      return "native";
    } catch (err) {
      // User dismissed the sheet — do NOT fall through to another flow.
      if (err && (err.name === "AbortError" || err.code === 20)) {
        return "native";
      }
      // Any other error → fall through to WhatsApp.
    }
  }

  // 3. WhatsApp deep-link fallback.
  const waUrl = options.text
    ? `https://wa.me/?text=${encodeURIComponent(`${options.text}\n${link}`)}`
    : buildWhatsAppShareUrl(code);
  if (waUrl && typeof window !== "undefined") {
    try {
      const w = window.open(waUrl, "_blank", "noopener,noreferrer");
      if (w) return "whatsapp";
    } catch {
      /* ignore, fall through */
    }
  }

  // 4. Clipboard fallback.
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(link);
      say("success", copyMessage);
      return "copy";
    } catch {
      /* ignore */
    }
  }

  say("error", "Could not open the share sheet. Please try again.");
  return "none";
}

/** Called ONCE per app boot from any page. Reads `?ref=` and `/join/CODE`,
 *  fires a background visit + attribute, then strips the ref param from the URL
 *  so it doesn't get shared/bookmarked. */
export async function captureReferralFromUrl() {
  if (typeof window === "undefined") return null;
  let code = null;
  try {
    const url = new URL(window.location.href);
    // 1) /join/CODE
    const joinMatch = url.pathname.match(/^\/join\/([A-Z0-9]{6})\/?$/i);
    if (joinMatch) code = joinMatch[1];
    // 2) ?ref=CODE (also honoured on /join for legacy)
    if (!code) code = url.searchParams.get("ref");
    if (!isValidCode(code)) return null;
    code = safeUpper(code);

    // Track a visit ONCE per session per code. This also acts as our
    // "is the code valid on the server" check — a 404 here means we skip
    // the local attribution entirely.
    const visitKey = SESSION_VISIT_PREFIX + code;
    let alreadyVisited = false;
    try {
      alreadyVisited = sessionStorage.getItem(visitKey) === "1";
    } catch {
      /* ignore */
    }

    let codeConfirmed = alreadyVisited; // in a prior tab we already validated
    if (!alreadyVisited) {
      try {
        await api.post("/referrals/visit", { code });
        codeConfirmed = true;
        try {
          sessionStorage.setItem(visitKey, "1");
        } catch {
          /* ignore */
        }
      } catch {
        // Unknown code or transient failure — don't attribute locally.
        codeConfirmed = false;
      }
    }

    // Only after the backend confirmed the code do we persist the attribution
    // and fire the (idempotent, non-overwriting) attribute call.
    if (codeConfirmed) {
      const stored = setReferredByOnce(code);
      if (stored) {
        api.post("/referrals/attribute", { code }).catch(() => {});
      }
    }

    // Clean the URL so the ref is not shared/bookmarked. `/join/CODE` is
    // handled by a dedicated welcome page (`JoinWelcome`) — keep that path
    // intact so the welcome UI stays visible. Only strip `?ref=` from any
    // other URL so hitting back/reload doesn't re-trigger the flow.
    try {
      if (!joinMatch) {
        url.searchParams.delete("ref");
        const search = url.searchParams.toString();
        const clean = url.pathname + (search ? `?${search}` : "") + url.hash;
        window.history.replaceState({}, "", clean);
      }
    } catch {
      /* ignore */
    }
    return code;
  } catch {
    return null;
  }
}
